import React, { useCallback, useEffect, useState } from 'react';
import { IPreviewResponse, IPromptView } from '../interfaces/IPrompt';
import {
  deletePrompt,
  getPromptHistory,
  listPrompts,
  previewPrompt,
  rollbackPrompt,
  savePrompt,
  suggestPrompt,
} from '../services/PromptAdminService';
import { IPromptVersion } from '../interfaces/IPrompt';
import style from './PromptAdminPage.module.scss';

const SIGNALS = [
  { value: 'VOLUME_DROP', label: 'Volumen unter Schnitt' },
  { value: 'VOLUME_SPIKE', label: 'Volumen über Schnitt' },
];
const PLACEHOLDERS = '{{zsb}} {{today}} {{avg}} {{delta}} {{sampleDays}}';

function signalLabel(s: string): string {
  const f = SIGNALS.find((x) => x.value === s);
  return f ? f.label : s;
}
function scopeLabel(scope: string): string {
  if (!scope || scope === 'default') return 'alle ZSB (Standard)';
  if (scope.startsWith('od:')) return `Depot ${scope.slice(3)}`;
  if (scope.startsWith('oz:')) return `ZSB ${scope.slice(3)}`;
  return scope;
}

type Draft = { signalType: string; scopeKey: string; name: string; template: string; isNew: boolean };

/** ISO instant -> "25.07.2026 04:30" for the version list. */
function formatVersionDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) {
    return iso;
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Prompt-Verwaltung — simple, name-driven editor for the SLM briefing prompts.
 * Left: the prompts by name. Click one to edit it (or "+ Neuer Prompt"). The type (signal) and
 * area (scope) are shown as context; they're only editable when creating a new prompt.
 */
export const PromptAdminPage: React.FC = () => {
  const [prompts, setPrompts] = useState<IPromptView[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [preview, setPreview] = useState<IPreviewResponse | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<IPromptVersion[]>([]);
  // Optional real-facts preview: a base + day whose stored finding supplies real numbers.
  const [previewOrg, setPreviewOrg] = useState('');
  const [previewDate, setPreviewDate] = useState('');

  const defaultTemplateFor = useCallback((list: IPromptView[], sig: string): string => {
    const def = list.find((p) => p.signalType === sig && p.scopeKey === 'default');
    return def ? def.template : '';
  }, []);

  const reload = useCallback((selectKey?: string, selectFirst?: boolean) => {
    listPrompts()
      .then((list) => {
        setPrompts(list);
        if (selectKey) {
          const found = list.find((p) => `${p.signalType}|${p.scopeKey}` === selectKey);
          if (found) {
            setDraft({ ...found, name: found.name || '', isNew: false });
          }
        } else if (selectFirst && list.length) {
          setDraft({ ...list[0], name: list[0].name || '', isNew: false });
        }
      })
      .catch((e) => setError(String(e && e.message ? e.message : e)));
  }, []);

  useEffect(() => {
    reload(undefined, true);
  }, [reload]);

  const loadHistory = useCallback((signalType: string, scopeKey: string) => {
    getPromptHistory(signalType, scopeKey)
      .then(setHistory)
      .catch(() => setHistory([]));
  }, []);

  const openPrompt = (p: IPromptView) => {
    setDraft({
      signalType: p.signalType,
      scopeKey: p.scopeKey,
      name: p.name || '',
      template: p.template,
      isNew: false,
    });
    setPreview(null);
    setMessage(null);
    setError(null);
    loadHistory(p.signalType, p.scopeKey);
  };

  const onRollback = (version: IPromptVersion) => {
    if (!draft || draft.isNew) return;
    setBusy('rollback');
    setMessage(null);
    setError(null);
    rollbackPrompt(draft.signalType, draft.scopeKey, version.updatedAt)
      .then(() => {
        setMessage('Version wiederhergestellt.');
        reload(`${draft.signalType}|${draft.scopeKey}`);
        loadHistory(draft.signalType, draft.scopeKey);
      })
      .catch((e) => setError(String(e && e.message ? e.message : e)))
      .finally(() => setBusy(null));
  };

  const newPrompt = () => {
    const sig = 'VOLUME_DROP';
    setDraft({ signalType: sig, scopeKey: '', name: '', template: defaultTemplateFor(prompts, sig), isNew: true });
    setPreview(null);
    setMessage(null);
    setError(null);
  };

  const patch = (p: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...p } : d));

  const run = (label: string, task: Promise<unknown>, onOk?: () => void) => {
    setBusy(label);
    setMessage(null);
    setError(null);
    task
      .then(() => onOk && onOk())
      .catch((e) => setError(String(e && e.message ? e.message : e)))
      .finally(() => setBusy(null));
  };

  const onSave = () => {
    if (!draft) return;
    if (!draft.scopeKey.trim() || !draft.template.trim()) {
      setError('Bereich und Vorlage dürfen nicht leer sein.');
      return;
    }
    const key = `${draft.signalType}|${draft.scopeKey.trim()}`;
    run(
      'save',
      savePrompt({
        signalType: draft.signalType,
        scopeKey: draft.scopeKey.trim(),
        name: draft.name,
        template: draft.template,
      }),
      () => {
        setMessage(`Gespeichert: ${draft.name || scopeLabel(draft.scopeKey)}`);
        reload(key);
      }
    );
  };

  const onDelete = () => {
    if (!draft) return;
    run('delete', deletePrompt(draft.signalType, draft.scopeKey), () => {
      setMessage('Gelöscht.');
      setDraft(null);
      reload(undefined, true);
    });
  };

  const onPreview = () => {
    if (!draft) return;
    setBusy('preview');
    setMessage(null);
    setError(null);
    previewPrompt(draft.signalType, draft.template, previewOrg.trim() || undefined, previewDate || undefined)
      .then(setPreview)
      .catch((e) => setError(String(e && e.message ? e.message : e)))
      .finally(() => setBusy(null));
  };

  const onSuggest = () => {
    if (!draft) return;
    setBusy('suggest');
    setMessage(null);
    setError(null);
    suggestPrompt(draft.signalType, draft.template)
      .then((s) => {
        if (s) {
          patch({ template: s });
          setMessage('KI-Vorschlag übernommen – bitte prüfen und ggf. anpassen.');
        }
      })
      .catch((e) => setError(String(e && e.message ? e.message : e)))
      .finally(() => setBusy(null));
  };

  const activeKey = draft && !draft.isNew ? `${draft.signalType}|${draft.scopeKey}` : '';

  return (
    <div className={style.page} data-testid='prompt-admin-page'>
      <div className={style.head}>
        <h1 className={style.title}>Prompt-Verwaltung</h1>
        <div className={style.subtitle}>
          KI-Vorlagen für die Briefings. Links einen Prompt wählen und bearbeiten – oder einen neuen anlegen.
        </div>
      </div>

      <div className={style.body}>
        <div className={style.listCol}>
          <div className={style.listHeadRow}>
            <span>Prompts</span>
            <button type='button' className={style.newBtn} onClick={newPrompt}>
              + Neuer Prompt
            </button>
          </div>
          <div className={style.promptList}>
            {prompts.map((p) => {
              const key = `${p.signalType}|${p.scopeKey}`;
              return (
                <div
                  key={key}
                  className={`${style.promptItem} ${activeKey === key ? style.activeItem : ''}`}
                  onClick={() => openPrompt(p)}>
                  <div className={style.promptName}>
                    {p.name || `${signalLabel(p.signalType)} – ${scopeLabel(p.scopeKey)}`}
                  </div>
                  <div className={style.promptSub}>
                    {signalLabel(p.signalType)} · {scopeLabel(p.scopeKey)}
                  </div>
                </div>
              );
            })}
            {prompts.length === 0 && <div className={style.muted}>Keine Prompts.</div>}
          </div>
        </div>

        <div className={style.editCol}>
          {!draft && <div className={style.muted}>Wähle links einen Prompt oder lege einen neuen an.</div>}
          {draft && (
            <React.Fragment>
              <label className={style.fieldFull}>
                <span>Name</span>
                <input
                  type='text'
                  value={draft.name}
                  placeholder='z. B. Volumen niedrig – Standard'
                  onChange={(e) => patch({ name: e.target.value })}
                />
              </label>

              {draft.isNew ? (
                <div className={style.fieldRow}>
                  <label className={style.field}>
                    <span>Typ</span>
                    <select
                      value={draft.signalType}
                      onChange={(e) =>
                        patch({ signalType: e.target.value, template: defaultTemplateFor(prompts, e.target.value) })
                      }>
                      {SIGNALS.map((s) => (
                        <option key={s.value} value={s.value}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className={style.field}>
                    <span>Bereich (default / od:Depot / oz:ZSB)</span>
                    <input
                      type='text'
                      value={draft.scopeKey}
                      placeholder='oz:12345'
                      onChange={(e) => patch({ scopeKey: e.target.value })}
                    />
                  </label>
                </div>
              ) : (
                <div className={style.contextRow}>
                  <span className={style.badgeInfo}>Typ: {signalLabel(draft.signalType)}</span>
                  <span className={style.badgeInfo}>Bereich: {scopeLabel(draft.scopeKey)}</span>
                  <span className={style.contextHint}>für einen anderen Bereich „+ Neuer Prompt" nutzen</span>
                </div>
              )}

              <label className={style.fieldFull}>
                <span>Vorlage — Platzhalter: {PLACEHOLDERS}</span>
                <textarea
                  className={style.textarea}
                  value={draft.template}
                  spellCheck={false}
                  onChange={(e) => patch({ template: e.target.value })}
                />
              </label>

              {/* Optional: preview against a real base + day instead of synthetic sample facts. */}
              <div className={style.previewInputs}>
                <label className={style.previewInputLabel}>
                  Vorschau mit echtem Standort (optional)
                  <input
                    className={style.previewInput}
                    type='text'
                    placeholder='oz:1067'
                    value={previewOrg}
                    onChange={(e) => setPreviewOrg(e.target.value)}
                  />
                </label>
                <input
                  className={style.previewInput}
                  type='date'
                  value={previewDate}
                  onChange={(e) => setPreviewDate(e.target.value)}
                />
              </div>

              <div className={style.actions}>
                <button type='button' className={style.primary} disabled={!!busy} onClick={onSave}>
                  {busy === 'save' ? 'Speichert…' : 'Speichern'}
                </button>
                <button type='button' className={style.secondary} disabled={!!busy} onClick={onPreview}>
                  {busy === 'preview' ? 'Vorschau…' : 'Vorschau (SLM)'}
                </button>
                <button type='button' className={style.secondary} disabled={!!busy} onClick={onSuggest}>
                  {busy === 'suggest' ? 'KI denkt… (bis ~1 min)' : 'KI-Vorschlag'}
                </button>
                {!draft.isNew && draft.scopeKey !== 'default' && (
                  <button type='button' className={style.danger} disabled={!!busy} onClick={onDelete}>
                    Löschen
                  </button>
                )}
              </div>

              {message && <div className={style.ok}>{message}</div>}
              {error && <div className={style.err}>Fehler: {error}</div>}

              {preview && (
                <div className={style.preview}>
                  <div className={style.previewHead}>
                    {preview.realFacts ? 'Vorschau (echte Daten)' : 'Vorschau (Beispiel-Daten)'}
                    <span className={preview.verified ? style.badgeOk : style.badgeWarn}>
                      {preview.verified ? 'Zahlen geprüft ✓' : 'Zahlen NICHT geprüft'}
                    </span>
                  </div>
                  <div className={style.previewTitle}>{preview.headline}</div>
                  <div className={style.previewText}>{preview.reason}</div>
                  <div className={style.previewReco}>➜ {preview.recommendation}</div>
                </div>
              )}

              {/* Version history + rollback — only for an existing prompt. */}
              {!draft.isNew && history.length > 0 && (
                <div className={style.history}>
                  <div className={style.historyHead}>Versionen ({history.length})</div>
                  {history.map((v) => (
                    <div key={v.updatedAt} className={style.historyRow}>
                      <span className={style.historyMeta}>
                        {formatVersionDate(v.updatedAt)}
                        {v.updatedBy ? ` · ${v.updatedBy}` : ''}
                        {v.action === 'DELETED' ? ' · gelöscht' : ''}
                      </span>
                      {v.action === 'SAVED' && (
                        <button
                          type='button'
                          className={style.historyRestore}
                          disabled={!!busy}
                          title='Diese Version wiederherstellen'
                          onClick={() => onRollback(v)}>
                          Wiederherstellen
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </React.Fragment>
          )}
        </div>
      </div>
    </div>
  );
};

export default PromptAdminPage;
